import React, { useState, useCallback } from 'react';
import { Select } from 'antd';

// The "Search in" dropdown shared by the lead pages. Each value is a group the
// backend (LeadController getAll, searchFields) knows; "all" is the full search
// over every lead field. A page sends searchFields only with a search.
export const SEARCH_FIELD_OPTIONS = [
  { value: 'quick', label: 'Quick', fields: 'Name, Email, Phone, ZIP, Order #, VIN' },
  { value: 'vehicle', label: 'Vehicle', fields: 'Make, Model, Part, Year' },
  { value: 'phone', label: 'Phone', fields: 'Phone number' },
  { value: 'order', label: 'Order #', fields: 'Order number' },
  { value: 'email', label: 'Email', fields: 'Email address' },
  { value: 'vin', label: 'VIN', fields: 'VIN' },
  { value: 'all', label: 'All fields', fields: 'Every lead field (slower)' }
];

export const DEFAULT_SEARCH_FIELDS = 'quick';

const PLACEHOLDERS = {
  quick: 'Search name, email, phone, ZIP, order #, VIN...',
  vehicle: 'Search make, model, part, year...',
  phone: 'Search phone number...',
  order: 'Search order #...',
  email: 'Search email...',
  vin: 'Search VIN...',
  all: 'Search all lead fields...'
};

export const getSearchFieldsPlaceholder = (value) =>
  PLACEHOLDERS[value] || PLACEHOLDERS[DEFAULT_SEARCH_FIELDS];

const isKnownSearchFields = (value) =>
  SEARCH_FIELD_OPTIONS.some(option => option.value === value);

// The choice is kept per page and per browser, so each page reopens with the
// group last used on it. Storage can be unavailable; the default is used then.
export const readSavedSearchFields = (storageKey) => {
  try {
    const saved = localStorage.getItem(storageKey);
    return isKnownSearchFields(saved) ? saved : DEFAULT_SEARCH_FIELDS;
  } catch {
    return DEFAULT_SEARCH_FIELDS;
  }
};

export const useSearchFields = (storageKey) => {
  const [searchFields, setSearchFields] = useState(() => readSavedSearchFields(storageKey));

  const changeSearchFields = useCallback((value) => {
    setSearchFields(value);
    try {
      localStorage.setItem(storageKey, value);
    } catch {
      // Storage unavailable: the choice lasts until the page closes.
    }
  }, [storageKey]);

  return [searchFields, changeSearchFields];
};

const renderOption = (option) => (
  <div style={{ lineHeight: 1.3, padding: '2px 0' }}>
    <div>{option.data.label}</div>
    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{option.data.fields}</div>
  </div>
);

const SearchFieldsSelect = ({ value, onChange, size = 'large', style }) => {
  const current = SEARCH_FIELD_OPTIONS.find(option => option.value === value);

  return (
    <Select
      value={value}
      onChange={onChange}
      options={SEARCH_FIELD_OPTIONS}
      optionRender={renderOption}
      size={size}
      title={current ? `Searches: ${current.fields}` : undefined}
      popupMatchSelectWidth={false}
      listHeight={400}
      aria-label="Search in"
      style={{ width: 120, flexShrink: 0, ...style }}
    />
  );
};

export default SearchFieldsSelect;
